import {
	type CreateTicketBody,
	TICKET_KINDS,
	TICKET_LIMITS,
	type TicketKind,
} from "@zplanem/responses"
import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { MentionPicker } from "../../../components/chat/mention-picker"
import { useMentionCompose } from "../../../components/chat/use-mention-compose"
import { ApiError } from "../../../lib/api-client"
import { useProjects } from "../../hooks/use-projects"
import { usePropertyMemberships } from "../../hooks/use-property-memberships"
import { useCreateTicket } from "../../hooks/use-tickets"
import { useTopics } from "../../hooks/use-topics"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useUser } from "../../user-context"
import type { TicketBoardFilters } from "./ticket-filter-model"
import { topicsVisibleForTicketScope } from "./ticket-filter-model"
import {
	buildAssigneeOptions,
	buildTicketMentionCandidates,
	ticketMentionRequestFields,
} from "./ticket-form-model"

const CONTROL_FOCUS =
	"focus:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

function fillTemplate(
	template: string,
	vars: Record<string, string | number>,
): string {
	return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""))
}

export function CreateTicketModal({
	propertyId,
	initialFilters = {},
	onClose,
}: {
	propertyId: string
	initialFilters?: TicketBoardFilters
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const memberships = usePropertyMemberships(propertyId)
	const projects = useProjects(propertyId)
	const topics = useTopics(propertyId)
	const createTicket = useCreateTicket(propertyId)
	const { profile } = useUser()
	const [title, setTitle] = useState("")
	const [description, setDescription] = useState("")
	const [kind, setKind] = useState<TicketKind>(initialFilters.kind ?? "task")
	const [projectId, setProjectId] = useState(initialFilters.projectId ?? "")
	const [topicId, setTopicId] = useState(initialFilters.topicId ?? "")
	const [assigneeUserId, setAssigneeUserId] = useState(
		initialFilters.assigneeId ?? "",
	)
	const [errors, setErrors] = useState<{
		title?: string
		description?: string
		form?: string
	}>({})
	const descriptionRef = useRef<HTMLTextAreaElement | null>(null)
	const descriptionMentionsId = "ticket-description-mentions"
	const mentionPeople = useMemo(
		() => buildTicketMentionCandidates(memberships.data, profile?.id),
		[memberships.data, profile?.id],
	)
	const descriptionMention = useMentionCompose({
		people: mentionPeople,
		draft: description,
		setDraft: setDescription,
		textareaRef: descriptionRef,
	})
	const visibleTopics = useMemo(
		() => topicsVisibleForTicketScope(topics.data ?? [], projectId || null),
		[topics.data, projectId],
	)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	useEffect(() => {
		if (
			topicId &&
			!topics.isLoading &&
			!visibleTopics.some((topic) => topic.id === topicId)
		) {
			setTopicId("")
		}
	}, [topicId, topics.isLoading, visibleTopics])

	function validate(): boolean {
		const next: typeof errors = {}
		const trimmedTitle = title.trim()
		if (trimmedTitle.length === 0) {
			next.title = t.tickets.formErrors.titleRequired
		} else if (trimmedTitle.length > TICKET_LIMITS.titleMax) {
			next.title = fillTemplate(t.tickets.formErrors.titleTooLong, {
				max: TICKET_LIMITS.titleMax,
			})
		}
		if (description.trim().length > TICKET_LIMITS.descriptionMax) {
			next.description = fillTemplate(t.tickets.formErrors.descriptionTooLong, {
				max: TICKET_LIMITS.descriptionMax,
			})
		}
		setErrors(next)
		return Object.keys(next).length === 0
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!validate()) return
		setErrors((prev) => ({ ...prev, form: undefined }))
		const trimmedDescription = description.trim()
		const descriptionMentions =
			descriptionMention.resolveMentions(trimmedDescription)
		const body: CreateTicketBody = {
			title: title.trim(),
			kind,
			...(trimmedDescription
				? {
						description: trimmedDescription,
						...ticketMentionRequestFields(descriptionMentions),
					}
				: {}),
			...(projectId ? { projectId } : {}),
			...(topicId ? { topicId } : {}),
			...(assigneeUserId ? { assigneeUserId } : {}),
		}
		try {
			await createTicket.mutateAsync(body)
			onClose()
		} catch (err) {
			const message =
				err instanceof ApiError ? err.message : t.tickets.formErrors.unknown
			setErrors((prev) => ({ ...prev, form: message }))
		}
	}

	const assigneeOptions = buildAssigneeOptions(
		memberships.data,
		t.tickets.unassigned,
	)

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="create-ticket-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={t.projects.form.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-border bg-popover p-6 text-popover-foreground shadow-2xl">
				<div className="flex items-start justify-between gap-3">
					<h2 id="create-ticket-title" className="text-xl font-bold">
						{t.tickets.createTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="px-2 py-1 text-muted-foreground hover:text-foreground"
						aria-label={t.projects.form.cancel}
					>
						×
					</button>
				</div>

				<form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate>
					<div>
						<label
							htmlFor="ticket-title"
							className="mb-2 block text-sm font-semibold text-foreground/80"
						>
							{t.tickets.titleLabel}
						</label>
						<input
							id="ticket-title"
							type="text"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder={t.tickets.titlePlaceholder}
							maxLength={TICKET_LIMITS.titleMax + 10}
							aria-invalid={Boolean(errors.title)}
							className={`w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground outline-none placeholder:text-muted-foreground dark:bg-muted/60 ${CONTROL_FOCUS}`}
						/>
						{errors.title ? (
							<p className="mt-1 text-sm text-destructive">{errors.title}</p>
						) : null}
					</div>

					<fieldset>
						<legend className="mb-2 block text-sm font-semibold text-foreground/80">
							{t.tickets.kindLabel}
						</legend>
						<div className="grid grid-cols-2 gap-2">
							{TICKET_KINDS.map((ticketKind) => (
								<label
									key={ticketKind}
									className={
										"flex cursor-pointer items-center justify-center rounded-xl border px-3 py-2.5 text-sm transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background " +
										(kind === ticketKind
											? "border-ring bg-accent text-foreground"
											: "border-border bg-muted/40 text-foreground/80 hover:border-foreground/25")
									}
								>
									<input
										type="radio"
										name="ticket-kind"
										value={ticketKind}
										checked={kind === ticketKind}
										onChange={() => setKind(ticketKind)}
										className="sr-only"
									/>
									<span>{t.tickets.kind[ticketKind]}</span>
								</label>
							))}
						</div>
					</fieldset>

					<div className="relative">
						<label
							id="ticket-description-label"
							htmlFor="ticket-description"
							className="mb-2 block text-sm font-semibold text-foreground/80"
						>
							{t.tickets.descriptionTitle}
						</label>
						<div
							role="combobox"
							aria-labelledby="ticket-description-label"
							aria-haspopup="listbox"
							aria-expanded={descriptionMention.open}
							aria-controls={
								descriptionMention.open ? descriptionMentionsId : undefined
							}
							aria-activedescendant={
								descriptionMention.open
									? `${descriptionMentionsId}-option-${descriptionMention.activeIndex}`
									: undefined
							}
							aria-autocomplete="list"
							className="relative"
							tabIndex={0}
						>
							{descriptionMention.open ? (
								<MentionPicker
									id={descriptionMentionsId}
									label={t.tickets.mentionSuggestionsLabel}
									candidates={descriptionMention.candidates}
									activeIndex={descriptionMention.activeIndex}
									onSelect={descriptionMention.select}
									onHover={descriptionMention.setActiveIndex}
								/>
							) : null}
							<textarea
								id="ticket-description"
								ref={descriptionRef}
								value={description}
								onChange={descriptionMention.onChange}
								onKeyDown={descriptionMention.onKeyDown}
								onKeyUp={descriptionMention.onCaretMove}
								onClick={descriptionMention.onCaretMove}
								rows={3}
								maxLength={TICKET_LIMITS.descriptionMax + 10}
								aria-invalid={Boolean(errors.description)}
								className={`w-full resize-y rounded-xl border border-border bg-muted px-4 py-3 text-foreground outline-none placeholder:text-muted-foreground dark:bg-muted/60 ${CONTROL_FOCUS}`}
							/>
						</div>
						{errors.description ? (
							<p className="mt-1 text-sm text-destructive">
								{errors.description}
							</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor="ticket-project"
							className="mb-2 block text-sm font-semibold text-foreground/80"
						>
							{t.tickets.filterProject}
						</label>
						<select
							id="ticket-project"
							value={projectId}
							onChange={(e) => setProjectId(e.target.value)}
							className={`w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground outline-none dark:bg-muted/60 ${CONTROL_FOCUS}`}
						>
							<option value="">{t.tickets.topicScopeProperty}</option>
							{(projects.data ?? []).map((project) => (
								<option key={project.id} value={project.id}>
									{project.name}
								</option>
							))}
						</select>
					</div>

					<div>
						<label
							htmlFor="ticket-topic"
							className="mb-2 block text-sm font-semibold text-foreground/80"
						>
							{t.tickets.filterTopic}
						</label>
						<select
							id="ticket-topic"
							value={topicId}
							onChange={(e) => setTopicId(e.target.value)}
							className={`w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground outline-none dark:bg-muted/60 ${CONTROL_FOCUS}`}
						>
							<option value="">{t.tickets.topicUngrouped}</option>
							{visibleTopics.map((topic) => (
								<option key={topic.id} value={topic.id}>
									{topic.name}
								</option>
							))}
						</select>
					</div>

					<div>
						<label
							htmlFor="ticket-assignee"
							className="mb-2 block text-sm font-semibold text-foreground/80"
						>
							{t.tickets.assigneeSelectLabel}
						</label>
						<select
							id="ticket-assignee"
							value={assigneeUserId}
							onChange={(e) => setAssigneeUserId(e.target.value)}
							className={`w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground outline-none dark:bg-muted/60 ${CONTROL_FOCUS}`}
						>
							{assigneeOptions.map((option) => (
								<option
									key={option.value || "__unassigned"}
									value={option.value}
								>
									{option.label}
								</option>
							))}
						</select>
					</div>

					{errors.form ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive bg-destructive px-3 py-2 text-sm text-destructive-foreground"
						>
							{errors.form}
						</div>
					) : null}

					<div className="flex justify-end gap-2 pt-2">
						<Button type="button" variant="ghost" onClick={onClose}>
							{t.projects.form.cancel}
						</Button>
						<Button
							type="submit"
							variant="brand"
							disabled={createTicket.isPending}
						>
							{createTicket.isPending
								? t.tickets.creating
								: t.tickets.submitCreate}
						</Button>
					</div>
				</form>
			</div>
		</div>
	)
}
